/**
 * DashboardOverview - Dashboard principal avec sections conditionnelles par plan
 * Utilise getUnified() comme source de données principale
 */

import {
  Wallet,
  Clock,
  AlertTriangle,
  TrendingUp,
  FileText,
  // CheckCircle2, // Not used
  // Timer, // Not used
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Repeat,
  DollarSign,
  BarChart2,
  Package,
  PieChart as PieChartIcon,
  Calendar,
  Sparkles,
} from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { businessMetricsAPI, invoicesAPI, quotesAPI } from '../utils/api';
import { logger } from '@/utils/logger';
import { recurringInvoicesAPI } from '../utils/recurringInvoicesAPI';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { PlanGate, UpgradeBanner } from './PlanGate';
import type { RecurringInvoiceForecastMonth } from '../features/billing/types/billing.types';

// AI Components (Phase 6 - CFO Virtuel) - Using Treasury module
import { AIInsightsPanel, HealthScoreGauge, useMLHealthScore } from '../features/treasury';

// ============================================================================
// TYPES
// ============================================================================

interface UnifiedStats {
  revenue: {
    totalHT: number;
    totalTVA: number;
    totalTTC: number;
    paidAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    counts: {
      total: number;
      paid: number;
      pending: number;
      overdue: number;
      draft: number;
    };
  };
  expenses: {
    totalHT: number;
    totalTVA: number;
    totalTTC: number;
    paidAmount: number;
    unpaidAmount: number;
    counts: {
      total: number;
      paid: number;
      pending: number;
      overdue: number;
    };
  };
  treasury: {
    currentBalance: number;
    expectedInflow: number;
    expectedOutflow: number;
    projectedBalance: number;
  };
  accounting: {
    revenue: { totalHT: number; totalTVA: number };
    expenses: { totalHT: number; totalTVA: number };
    vatBalance: number;
    grossProfit: number;
    grossMargin: number;
  };
  recurring: {
    mrr: number;
    arr: number;
    activeTemplates: number;
    pausedTemplates: number;
    nextMonthProjection: number;
  };
  period: string;
  generatedAt: string;
  cachedUntil: string;
}

interface RevenueEvolution {
  periode: string;
  caRealise: number;
  nbFactures: number;
  caPrevisionnel: number;
  nbDevis: number;
  chargesHt?: number;
  nbDepenses?: number;
}

interface CommercialMetrics {
  totalQuotes: number;
  acceptedQuotes: number;
  conversionRate: number;
  currentMonthQuotes: number;
  totalInvoices: number;
  paidInvoices: number;
  avgInvoiceAmount: number;
}

interface ProductPerformance {
  name: string;
  sales: number;
  revenue: number;
}

interface ProductCategory {
  category: string;
  count: number;
  percentage: number;
}

interface RecentActivityItem {
  id: string;
  type: 'invoice' | 'quote' | 'payment';
  title: string;
  amount: number;
  date: Date;
  status: 'pending' | 'completed' | 'overdue';
}

interface OutletContextType {
  user?: {
    id: string;
    email: string;
    companyId?: string;
    tenantId?: string;
  };
}

// ============================================================================
// COMPONENTS
// ============================================================================

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  icon: React.ReactNode;
  variant?: 'default' | 'alert' | 'success' | 'purple';
  delay?: number;
  loading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  variant = 'default',
  delay = 0,
  loading = false,
}) => {
  const variantClasses = {
    default: 'bg-gradient-to-br from-teal-50 to-teal-100/50 text-teal-600',
    alert: 'bg-gradient-to-br from-red-50 to-red-100/50 text-red-600',
    success: 'bg-gradient-to-br from-green-50 to-green-100/50 text-green-600',
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100/50 text-purple-600',
  };

  const valueClasses = {
    default: 'text-gray-900',
    alert: 'text-red-600',
    success: 'text-green-600',
    purple: 'text-purple-600',
  };

  return (
    <div
      className="group bg-white/20 backdrop-blur-xl rounded-3xl p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 animate-fade-in-up shadow-xl shadow-gray-300/10 border border-white/30"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-2xl ${variantClasses[variant]} shadow-sm`}>
          {icon}
        </div>
        {trend && !loading && (
          <div className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
            trend.isPositive ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-700'
          }`}>
            {trend.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trend.value}
          </div>
        )}
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1 tracking-wide uppercase">{title}</p>
        {loading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <p className={`text-2xl font-bold tracking-tight ${valueClasses[variant]}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </>
        )}
      </div>
    </div>
  );
};

interface PeriodSelectorProps {
  value: '7d' | '30d' | '90d' | '365d';
  onChange: (period: '7d' | '30d' | '90d' | '365d') => void;
  availablePeriods: ('7d' | '30d' | '90d' | '365d')[];
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({ value, onChange, availablePeriods }) => {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    '7d': t('dashboard.periods.7d'),
    '30d': t('dashboard.periods.30d'),
    '90d': t('dashboard.periods.90d'),
    '365d': t('dashboard.periods.365d'),
  };

  return (
    <div className="flex items-center gap-2 bg-white/70 backdrop-blur-xl rounded-xl p-1.5 border border-gray-200 shadow-sm">
      <Calendar className="w-4 h-4 text-gray-500 ml-2" />
      {availablePeriods.map((period) => (
        <button
          key={period}
          onClick={() => onChange(period)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
            value === period
              ? 'bg-teal-600 text-white shadow-sm'
              : 'text-gray-600 hover:bg-white/50'
          }`}
        >
          {labels[period]}
        </button>
      ))}
    </div>
  );
};

const ActivityItem: React.FC<{
  type: 'invoice' | 'payment' | 'quote';
  title: string;
  amount: string;
  time: string;
  status: 'pending' | 'completed' | 'overdue';
}> = ({ title, amount, time, status }) => {
  const statusColors = {
    pending: 'bg-gray-100 text-gray-700',
    completed: 'bg-teal-50 text-teal-700',
    overdue: 'bg-red-50 text-red-700',
  };

  const { t } = useTranslation();
  const statusLabels = {
    pending: t('dashboard.status.pending'),
    completed: t('dashboard.status.paid'),
    overdue: t('dashboard.status.overdue'),
  };

  const dotColors = {
    pending: 'bg-gray-400',
    completed: 'bg-teal-500',
    overdue: 'bg-red-500',
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100/50 last:border-0">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-2 h-2 rounded-full ${dotColors[status]}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[status]}`}>
              {statusLabels[status]}
            </span>
            <span className="text-xs text-gray-500">{time}</span>
          </div>
        </div>
      </div>
      <p className="text-sm font-bold text-gray-900 ml-3">{amount}</p>
    </div>
  );
};

// Dashboard Health Score Widget
const DashboardHealthScore: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { t } = useTranslation();
  const { data: healthScore, isLoading } = useMLHealthScore(companyId);

  return (
    <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 transition-all duration-500">
      <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">{t('dashboard.financialHealth')}</h3>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : healthScore ? (
        <div className="flex flex-col items-center">
          <HealthScoreGauge score={healthScore.overallScore} trend={healthScore.trend as 'UP' | 'STABLE' | 'DOWN'} size="lg" />
          {healthScore.recommendations?.[0] && (
            <div className="mt-4 w-full">
              <p className="text-xs font-medium text-gray-500 mb-1">{t('dashboard.aiAdvice')}</p>
              <p className="text-sm text-gray-700 bg-teal-50 rounded-lg p-2">{healthScore.recommendations[0]}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-center py-6 text-gray-500 text-sm">{t('dashboard.noDataShort')}</p>
      )}
    </div>
  );
};

// ============================================================================
// UTILITIES
// ============================================================================

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

const getRelativeTime = (date: Date, t: (key: string, opts?: Record<string, unknown>) => string): string => {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return t('dashboard.relativeTime.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('dashboard.relativeTime.hoursAgo', { count: diffHours });
  if (diffDays === 1) return t('dashboard.relativeTime.yesterday');
  if (diffDays < 7) return t('dashboard.relativeTime.daysAgo', { count: diffDays });
  return new Date(date).toLocaleDateString('fr-FR');
};

const CHART_COLORS = ['#0F766E', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#CCFBF1'];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DashboardOverview: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();
  const { features, planCode, isTrialActive: _isTrialActive, trialDaysRemaining, canUpgrade, loading: planLoading } = usePlanFeatures();

  // State
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | '365d'>('30d');
  const [unified, setUnified] = useState<UnifiedStats | null>(null);
  const [revenueEvolution, setRevenueEvolution] = useState<RevenueEvolution[]>([]);
  const [commercial, setCommercial] = useState<CommercialMetrics | null>(null);
  const [topProducts, setTopProducts] = useState<ProductPerformance[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [recurringForecast, setRecurringForecast] = useState<RecurringInvoiceForecastMonth[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);

  // Fetch data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Core APIs (always fetch)
        const corePromises: Promise<any>[] = [
          businessMetricsAPI.getUnified(period),
          invoicesAPI.getAll(),
          quotesAPI.getAll(),
        ];

        // Conditional APIs based on plan
        if (features.revenueChart) {
          corePromises.push(businessMetricsAPI.getRevenueEvolution(features.revenueChartMonths));
        }
        if (features.commercialPerformance) {
          corePromises.push(businessMetricsAPI.getCommercial());
        }
        if (features.productsAnalytics) {
          corePromises.push(businessMetricsAPI.getProductsPerformance(5));
          corePromises.push(businessMetricsAPI.getProductsCategories());
        }

        const results = await Promise.allSettled(corePromises);

        // Process unified stats (always first)
        if (results[0].status === 'fulfilled') {
          const unifiedData = results[0].value?.data || results[0].value;
          if (unifiedData) {
            setUnified(unifiedData);

            // Fetch recurring forecast if templates exist
            if (unifiedData.recurring?.activeTemplates > 0 && features.recurringRevenue) {
              try {
                const forecast = await recurringInvoicesAPI.getForecast(6);
                if (forecast?.monthly) {
                  setRecurringForecast(forecast.monthly);
                }
              } catch (e) {
                logger.warn('Could not fetch recurring forecast:', e);
              }
            }
          }
        }

        // Process invoices and quotes for activity
        const activities: RecentActivityItem[] = [];
        if (results[1].status === 'fulfilled') {
          const invoices = Array.isArray(results[1].value) ? results[1].value : [];
          invoices.slice(0, 5).forEach((inv: any) => {
            activities.push({
              id: inv.id,
              type: 'invoice',
              title: t('dashboard.activity.invoiceLabel', { number: inv.number || inv.invoiceNumber || '#' + inv.id.slice(0, 6) }),
              amount: parseFloat(inv.totalTTC || inv.total || 0),
              date: new Date(inv.issueDate || inv.createdAt),
              status: inv.status === 'PAID' ? 'completed' : inv.status === 'OVERDUE' ? 'overdue' : 'pending',
            });
          });
        }
        if (results[2].status === 'fulfilled') {
          const quotes = Array.isArray(results[2].value) ? results[2].value : [];
          quotes.slice(0, 3).forEach((quote: any) => {
            activities.push({
              id: quote.id,
              type: 'quote',
              title: t('dashboard.activity.quoteLabel', { number: quote.number || quote.quoteNumber || '#' + quote.id.slice(0, 6) }),
              amount: parseFloat(quote.totalTTC || quote.total || 0),
              date: new Date(quote.createdAt),
              status: quote.status === 'ACCEPTED' ? 'completed' : 'pending',
            });
          });
        }
        activities.sort((a, b) => b.date.getTime() - a.date.getTime());
        setRecentActivity(activities.slice(0, 5));

        // Process conditional data with proper type narrowing
        let resultIndex = 3;
        if (features.revenueChart) {
          const result = results[resultIndex];
          if (result?.status === 'fulfilled') {
            setRevenueEvolution((result as PromiseFulfilledResult<any>).value || []);
          }
          resultIndex++;
        }
        if (features.commercialPerformance) {
          const result = results[resultIndex];
          if (result?.status === 'fulfilled') {
            setCommercial((result as PromiseFulfilledResult<any>).value);
          }
          resultIndex++;
        }
        if (features.productsAnalytics) {
          const topResult = results[resultIndex];
          if (topResult?.status === 'fulfilled') {
            setTopProducts((topResult as PromiseFulfilledResult<any>).value || []);
          }
          resultIndex++;
          const catResult = results[resultIndex];
          if (catResult?.status === 'fulfilled') {
            setCategories((catResult as PromiseFulfilledResult<any>).value || []);
          }
        }

      } catch (error) {
        logger.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [period, features]);

  // Computed values
  const revenue = unified?.revenue;
  const recurring = unified?.recurring;

  // Chart data for revenue evolution
  const chartData = useMemo(() => {
    if (!revenueEvolution.length) return [];
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return revenueEvolution.map((item) => {
      const [, month] = item.periode.split('-');
      return {
        name: monthNames[parseInt(month, 10) - 1] || item.periode,
        caRealise: item.caRealise,
        caPrevisionnel: item.caPrevisionnel,
        nbFactures: item.nbFactures,
        nbDevis: item.nbDevis,
      };
    });
  }, [revenueEvolution]);

  return (
    <div className="min-h-screen relative">
      <div className="px-6 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-gray-900 mb-1">{t('dashboard.title')}</h1>
            <p className="text-sm text-gray-500">{t('dashboard.subtitle')}</p>
          </div>
          {features.periodSelector && (
            <PeriodSelector
              value={period}
              onChange={setPeriod}
              availablePeriods={features.availablePeriods}
            />
          )}
        </div>

        {/* Upgrade Banner */}
        {canUpgrade && (
          <UpgradeBanner planCode={planCode} trialDaysRemaining={trialDaysRemaining} loading={planLoading} />
        )}

        {/* SECTION 1: KPIs Essentiels (TOUS LES PLANS) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title={t('dashboard.metrics.paymentsReceived')}
            value={formatCurrency(revenue?.paidAmount || 0)}
            icon={<Wallet className="w-5 h-5" />}
            delay={0}
            loading={loading}
          />
          <MetricCard
            title={t('dashboard.metrics.toCollect')}
            value={formatCurrency(revenue?.pendingAmount || 0)}
            subtitle={revenue?.counts.pending ? t('dashboard.commercial.invoiceCount', { count: revenue.counts.pending, plural: revenue.counts.pending > 1 ? 's' : '' }) : undefined}
            icon={<Clock className="w-5 h-5" />}
            delay={100}
            loading={loading}
          />
          <MetricCard
            title={t('dashboard.metrics.unpaid')}
            value={formatCurrency(revenue?.overdueAmount || 0)}
            subtitle={revenue?.counts.overdue ? t('dashboard.commercial.overdueCount', { count: revenue.counts.overdue }) : undefined}
            icon={<AlertTriangle className="w-5 h-5" />}
            variant={revenue?.overdueAmount ? 'alert' : 'default'}
            delay={200}
            loading={loading}
          />
          <MetricCard
            title={t('dashboard.metrics.monthlyCA')}
            value={formatCurrency(revenue?.totalTTC || 0)}
            subtitle={revenue?.counts.total ? t('dashboard.commercial.invoicesCount', { count: revenue.counts.total }) : undefined}
            icon={<TrendingUp className="w-5 h-5" />}
            variant="success"
            delay={300}
            loading={loading}
          />
        </div>

        {/* SECTION 2: Performance Commerciale (PRO+) - KPIs importants */}
        <PlanGate
          feature="commercialPerformance"
          features={features}
          requiredPlan="PRO"
          sectionTitle={t('dashboard.planGate.commercialPerformance')}
          sectionDescription={t('dashboard.planGate.commercialDescription')}
          loading={planLoading}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title={t('dashboard.metrics.conversionRate')}
              value={formatPercent(commercial?.conversionRate || 0)}
              subtitle={t('dashboard.commercial.devisCount', { accepted: commercial?.acceptedQuotes || 0, total: commercial?.totalQuotes || 0 })}
              icon={<TrendingUp className="w-5 h-5" />}
              delay={0}
              loading={loading}
            />
            <MetricCard
              title={t('dashboard.metrics.avgBasket')}
              value={formatCurrency(commercial?.avgInvoiceAmount || 0)}
              subtitle={t('dashboard.commercial.perPaidInvoice')}
              icon={<DollarSign className="w-5 h-5" />}
              delay={100}
              loading={loading}
            />
            <MetricCard
              title={t('dashboard.metrics.paidInvoices')}
              value={String(commercial?.paidInvoices || 0)}
              subtitle={t('dashboard.commercial.outOfTotal', { total: commercial?.totalInvoices || 0 })}
              icon={<FileText className="w-5 h-5" />}
              variant="success"
              delay={200}
              loading={loading}
            />
            <MetricCard
              title={t('dashboard.metrics.quotesThisMonth')}
              value={String(commercial?.currentMonthQuotes || 0)}
              subtitle={t('dashboard.commercial.currentMonth')}
              icon={<BarChart2 className="w-5 h-5" />}
              variant="purple"
              delay={300}
              loading={loading}
            />
          </div>
        </PlanGate>

        {/* SECTION 3: Graphique CA + Activité (STARTER+) */}
        <PlanGate
          feature="revenueChart"
          features={features}
          requiredPlan="STARTER"
          sectionTitle={t('dashboard.planGate.revenueEvolution')}
          sectionDescription={t('dashboard.planGate.revenueDescription')}
          loading={planLoading}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Revenue Chart */}
            <div className="lg:col-span-2 bg-white/20 backdrop-blur-xl rounded-3xl p-6 shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-teal-600" />
                {t('dashboard.charts.revenueEvolutionMonths', { months: features.revenueChartMonths })}
              </h3>
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fill: '#6b7280', fontSize: 12 }} />
                    <Tooltip formatter={(value: number) => [formatCurrency(value), '']} />
                    <Legend />
                    <Line type="monotone" dataKey="caRealise" stroke="#10b981" strokeWidth={3} name={t('dashboard.caRealized')} dot={{ fill: '#10b981', r: 4 }} />
                    <Line type="monotone" dataKey="caPrevisionnel" stroke="#14b8a6" strokeWidth={2} strokeDasharray="5 5" name={t('dashboard.caForecast')} dot={{ fill: '#14b8a6', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <p>{t('dashboard.noData')}</p>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('dashboard.sections.recentActivity')}</h3>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : recentActivity.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">{t('dashboard.activity.noActivity')}</p>
              ) : (
                <div className="space-y-1">
                  {recentActivity.map((activity) => (
                    <ActivityItem
                      key={activity.id}
                      type={activity.type}
                      title={activity.title}
                      amount={formatCurrency(activity.amount)}
                      time={getRelativeTime(activity.date, t)}
                      status={activity.status}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </PlanGate>

        {/* SECTION 4: MRR / Revenus Récurrents (PRO+) */}
        {recurring && recurring.activeTemplates > 0 && (
          <PlanGate
            feature="recurringRevenue"
            features={features}
            requiredPlan="PRO"
            sectionTitle={t('dashboard.planGate.recurringRevenue')}
            sectionDescription={t('dashboard.planGate.recurringDescription')}
            loading={planLoading}
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title={t('dashboard.metrics.mrr')}
                  value={formatCurrency(recurring.mrr)}
                  icon={<Repeat className="w-5 h-5" />}
                  delay={0}
                  loading={loading}
                />
                <MetricCard
                  title={t('dashboard.metrics.arr')}
                  value={formatCurrency(recurring.arr)}
                  icon={<DollarSign className="w-5 h-5" />}
                  delay={100}
                  loading={loading}
                />
                <MetricCard
                  title={t('dashboard.metrics.activeTemplates')}
                  value={String(recurring.activeTemplates)}
                  subtitle={recurring.pausedTemplates > 0 ? t('dashboard.commercial.pausedCount', { count: recurring.pausedTemplates }) : undefined}
                  icon={<FileText className="w-5 h-5" />}
                  delay={200}
                  loading={loading}
                />
                <MetricCard
                  title={t('dashboard.metrics.nextMonthProjection')}
                  value={formatCurrency(recurring.nextMonthProjection)}
                  icon={<TrendingUp className="w-5 h-5" />}
                  delay={300}
                  loading={loading}
                />
              </div>

              {/* Forecast Chart */}
              {recurringForecast.length > 0 && (
                <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">{t('dashboard.charts.projection6months')}</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={recurringForecast}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fill: '#6b7280', fontSize: 12 }} />
                      <Tooltip formatter={(value: number) => [formatCurrency(value), t('dashboard.recurring.amountTTC')]} />
                      <Bar dataKey="projectedTTC" fill="#0F766E" radius={[8, 8, 0, 0]} name={t('dashboard.recurring.amountTTC')} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </PlanGate>
        )}

        {/* SECTION 5: Produits & Catégories (PRO+) */}
        <PlanGate
          feature="productsAnalytics"
          features={features}
          requiredPlan="PRO"
          sectionTitle={t('dashboard.planGate.productAnalysis')}
          sectionDescription={t('dashboard.planGate.productDescription')}
          loading={planLoading}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Products */}
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-600" />
                {t('dashboard.charts.top5Products')}
              </h3>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : topProducts.length === 0 ? (
                <p className="text-center py-8 text-gray-500">{t('dashboard.noDataShort')}</p>
              ) : (
                <div className="space-y-3">
                  {topProducts.map((product, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/40 backdrop-blur-sm rounded-xl border border-white/50 hover:bg-white/70 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-default">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-white/60 text-gray-700 border border-white/50`}>
                          #{index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-xs text-gray-500">{t('dashboard.commercial.salesCount', { count: product.sales })}</p>
                        </div>
                      </div>
                      <p className="font-bold text-gray-900">{formatCurrency(product.revenue)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Categories Pie Chart */}
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-teal-600" />
                {t('dashboard.charts.categoryBreakdown')}
              </h3>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : categories.length === 0 ? (
                <p className="text-center py-8 text-gray-500">{t('dashboard.noDataShort')}</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={categories as any[]}
                        dataKey="count"
                        nameKey="category"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ percent }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                      >
                        {categories.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {categories.slice(0, 4).map((cat, index) => (
                      <div key={cat.category} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS[index] }} />
                          <span className="text-gray-700">{cat.category}</span>
                        </div>
                        <span className="font-medium text-gray-900">{t('dashboard.commercial.productsCount', { count: cat.count })}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </PlanGate>

        {/* SECTION 6: AI Insights (ENTERPRISE) */}
        {user?.companyId && (
          <PlanGate
            feature="aiInsights"
            features={features}
            requiredPlan="ENTERPRISE"
            sectionTitle={t('dashboard.planGate.aiInsights')}
            sectionDescription={t('dashboard.planGate.aiDescription')}
            loading={planLoading}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <AIInsightsPanel companyId={user.companyId} />
              </div>
              <DashboardHealthScore companyId={user.companyId} />
            </div>
          </PlanGate>
        )}

        {/* FOOTER CTA */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-lime-400/20 rounded-full blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white mb-1">
                {canUpgrade ? t('dashboard.cta.unlockFeatures') : t('dashboard.cta.createNextQuote')}
              </h3>
              <p className="text-teal-100 text-sm">
                {canUpgrade
                  ? t('dashboard.cta.upgradeDescription')
                  : t('dashboard.cta.createQuoteDescription')}
              </p>
            </div>
            <button
              onClick={() => navigate(canUpgrade ? '/pricing' : '/quotes/new')}
              className="bg-white text-teal-700 px-6 py-3 rounded-xl font-bold hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center gap-2"
            >
              {canUpgrade ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t('dashboard.cta.viewPlans')}
                </>
              ) : (
                t('dashboard.cta.newQuote')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
